"use client";

import { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import {
  IconAlertCircle,
  IconArrowRight,
  IconArrowUp,
  IconCalendar,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconMail,
  IconPhone,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type {
  LegalPageContent,
  LegalContentBlock,
} from "@/content/legal/types";

interface LegalPageRendererProps {
  content: LegalPageContent;
  /**
   * @deprecated Ignored. The renderer paints with the theme's semantic tokens,
   * so it follows the site's light/dark palette on its own. Kept so existing
   * pages still compile; drop it from the call site.
   */
  variant?: "light" | "dark";
  /** Sticky row of section links under the header. */
  showTopNav?: boolean;
}

/** "1. Operator and contact" -> "Operator and contact" */
const displayTitle = (title: string) => title.replace(/^\d+\.\s*/, "").trim();

/** The policy permalinks the footer also lists (Terms ids are stable, see terms-of-service.ts). */
const LEGAL_LINKS = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Delivery", href: "/terms#delivery-and-availability" },
  { label: "Refunds", href: "/terms#refunds" },
  { label: "Disputes", href: "/terms#dispute-resolution" },
] as const;

/** `tel:` target: digits and a leading plus only. */
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

/**
 * Renders a legal page (Terms, Privacy, etc.): header, table of contents,
 * numbered section cards and a contact band. Colors come only from theme
 * tokens (background/card/muted/border/primary/destructive), so the page sits
 * inside any brand palette without its own light or dark skin.
 */
export function LegalPageRenderer({
  content,
  showTopNav = true,
}: LegalPageRendererProps) {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const phone = content.supportPhone || null;

  const sections = useMemo(() => {
    const seenIds = new Set<string>();
    const seenTitles = new Set<string>();

    return content.sections.filter((section) => {
      const title = displayTitle(section.title).toLowerCase();
      if (seenIds.has(section.id) || seenTitles.has(title)) return false;
      seenIds.add(section.id);
      seenTitles.add(title);
      return true;
    });
  }, [content.sections]);

  // Track scroll position for active section and back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);

      const sectionElements = sections
        .map((s) => document.getElementById(s.id))
        .filter((el): el is HTMLElement => el !== null);

      if (sectionElements.length === 0) return;

      let currentSection = sections[0]?.id ?? "";
      for (let i = 0; i < sectionElements.length; i++) {
        const rect = sectionElements[i].getBoundingClientRect();
        if (rect.top <= 150) {
          currentSection = sections[i]?.id ?? currentSection;
        } else {
          break;
        }
      }

      setActiveSection(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-sm text-muted-foreground mb-10"
          >
            <Link href="/" className="transition-colors hover:text-foreground">
              Home
            </Link>
            <IconChevronRight className="size-4" aria-hidden />
            <span className="font-medium text-foreground">{content.title}</span>
          </nav>

          <div className="max-w-4xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              {content.title}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              {content.description}
            </p>
          </div>

          <div className="mt-10 inline-flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-card-foreground">
            <IconCalendar className="size-4 text-primary" aria-hidden />
            <span className="text-sm text-muted-foreground">Last updated</span>
            <span className="text-sm font-semibold">{content.lastUpdated}</span>
            {content.version && (
              <span className="text-sm text-muted-foreground">
                · v{content.version}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Sticky Quick Navigation */}
      {showTopNav && (
        <div className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-none">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={cn(
                    "shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    activeSection === section.id
                      ? "bg-card text-card-foreground border border-border"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {displayTitle(section.title)}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 py-12 md:py-16">
        <div className="grid lg:grid-cols-[260px_1fr] gap-10">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-5 text-card-foreground">
              <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                Contents
              </h2>
              <nav aria-label="Sections" className="mt-4 space-y-1">
                {sections.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      aria-current={isActive ? "location" : undefined}
                      className={cn(
                        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-background text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "size-1.5 shrink-0 rounded-full",
                          isActive ? "bg-primary" : "bg-border",
                        )}
                      />
                      <span className="line-clamp-1">
                        {displayTitle(section.title)}
                      </span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="min-w-0 space-y-6">
            {sections.map((section, sectionIdx) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-24 rounded-2xl border border-border bg-card text-card-foreground"
              >
                <div className="flex items-center gap-4 border-b border-border px-6 py-5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-xs font-semibold text-foreground tabular-nums">
                    {String(sectionIdx + 1).padStart(2, "0")}
                  </span>
                  <h2 className="text-xl font-semibold text-card-foreground">
                    {displayTitle(section.title)}
                  </h2>
                </div>
                <div className="space-y-4 px-6 py-6">
                  {section.content.map((block, idx) => (
                    <ContentBlock key={idx} block={block} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      {/* Contact band */}
      <section className="border-t border-border bg-card text-card-foreground">
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold mb-2">
                Questions about this policy?
              </h2>
              <p className="text-muted-foreground">
                Contact us at{" "}
                <a
                  href={`mailto:${content.supportEmail}`}
                  className="text-card-foreground underline underline-offset-4 decoration-border hover:decoration-current transition-colors"
                >
                  {content.supportEmail}
                </a>
                {phone && (
                  <>
                    {" "}or by phone at{" "}
                    <a
                      href={telHref(phone)}
                      className="text-card-foreground underline underline-offset-4 decoration-border hover:decoration-current transition-colors"
                    >
                      {phone}
                    </a>
                  </>
                )}
                .
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-xl px-6 h-12">
                <a
                  href={`mailto:${content.supportEmail}`}
                  className="inline-flex items-center gap-2"
                >
                  <IconMail className="size-4" aria-hidden />
                  Email support
                </a>
              </Button>
              {phone && (
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-xl px-6 h-12"
                >
                  <a href={telHref(phone)} className="inline-flex items-center gap-2">
                    <IconPhone className="size-4" aria-hidden />
                    Call us
                  </a>
                </Button>
              )}
            </div>
          </div>
          <nav
            aria-label="Legal documents"
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-6 text-sm"
          >
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-card-foreground"
              >
                {link.label}
                <IconArrowRight className="size-3.5" aria-hidden />
              </Link>
            ))}
          </nav>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        type="button"
        onClick={scrollToTop}
        className={cn(
          "fixed bottom-8 right-8 z-50 p-3 rounded-xl border border-border bg-card text-muted-foreground",
          "hover:text-foreground shadow-lg transition-all duration-300",
          showBackToTop
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none",
        )}
        aria-label="Back to top"
      >
        <IconArrowUp className="size-5" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Renders individual content blocks
 */
function ContentBlock({ block }: { block: LegalContentBlock }) {
  const nosnippet = block.nosnippet ? { "data-nosnippet": true } : {};

  switch (block.type) {
    case "paragraph":
      return (
        <p className="leading-relaxed text-card-foreground" {...nosnippet}>
          {block.text}
        </p>
      );

    case "subheading":
      return (
        <h3
          className="mt-6 mb-3 flex items-center gap-2 font-semibold text-card-foreground"
          {...nosnippet}
        >
          <span aria-hidden className="size-1.5 rounded-full bg-primary" />
          {block.text}
        </h3>
      );

    case "list": {
      const cross = block.marker === "cross";
      const Marker = cross ? IconCircleX : IconCircleCheck;
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag className="space-y-2" {...nosnippet}>
          {block.items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-card-foreground">
              <Marker
                aria-hidden
                className={cn(
                  "size-5 shrink-0 mt-0.5",
                  cross ? "text-destructive" : "text-primary",
                )}
              />
              <span>{item}</span>
            </li>
          ))}
        </ListTag>
      );
    }

    case "highlight":
      return (
        <div
          className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/10 p-4"
          {...nosnippet}
        >
          <IconAlertCircle className="size-5 shrink-0 mt-0.5 text-primary" aria-hidden />
          <p className="text-sm leading-relaxed text-foreground">{block.text}</p>
        </div>
      );

    case "definition":
      return (
        <dl className="rounded-xl border border-border bg-background p-4" {...nosnippet}>
          <dt className="mb-1 font-semibold text-foreground">{block.term}</dt>
          <dd className="text-sm text-muted-foreground">{block.definition}</dd>
        </dl>
      );

    default:
      return null;
  }
}
